import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CreditCard, FileText, MapPin, User } from 'lucide-react';
import { useCartStore } from '@/stores/cartStore';
import { useAuthStore } from '@/stores/authStore';
import { api, clienteToCheckoutForm, formatMXN } from '@/lib/api';
import { calcularIva, totalConIva } from '@/lib/precios';
import { CatalogBanner } from '@/components/layout/CatalogBanner';
import { Panel } from '@/components/ui';
import { OpenpayCheckoutSection } from '@/components/checkout/OpenpayCheckoutSection';
import { CheckoutModeSelector } from '@/components/checkout/CheckoutModeSelector';
import type { CheckoutForm, ModoCompra } from '@/types';

const GUEST_EMAIL_KEY = 'alarcom_checkout_guest_email';

const initialForm: CheckoutForm = {
  nombre: '',
  email: '',
  telefono: '',
  calle: '',
  numero: '',
  colonia: '',
  municipio: '',
  estado: '',
  codigo_postal: '',
  requiere_factura: false,
};

export function CheckoutPage() {
  const { items, subtotal, clearCart } = useCartStore();
  const { cliente, token, refresh } = useAuthStore();
  const loggedIn = !!token;
  const [searchParams] = useSearchParams();
  const openpayReturnId = searchParams.get('id');
  const [modo, setModo] = useState<ModoCompra>('invitado');
  const [form, setForm] = useState<CheckoutForm>(initialForm);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<{
    folio: string;
    idVenta?: number;
    pagado?: boolean;
    email?: string;
    altaInvitado?: boolean;
  } | null>(null);
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [confirmando3ds, setConfirmando3ds] = useState(!!openpayReturnId);
  const [correoExistente, setCorreoExistente] = useState(false);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (cliente) {
      setModo('cliente');
      setForm(clienteToCheckoutForm(cliente));
      setCorreoExistente(false);
    }
  }, [cliente]);

  useEffect(() => {
    if (modo === 'invitado' && form.email) {
      sessionStorage.setItem(GUEST_EMAIL_KEY, form.email.trim().toLowerCase());
    }
  }, [modo, form.email]);

  useEffect(() => {
    if (!openpayReturnId) return;
    let cancelled = false;
    setConfirmando3ds(true);
    setError(null);
    api
      .openpayConfirm(openpayReturnId)
      .then(async (result) => {
        if (cancelled) return;
        const emailGuardado = sessionStorage.getItem(GUEST_EMAIL_KEY) || '';
        setExito({
          folio: result.folio,
          idVenta: result.id_venta,
          pagado: true,
          email: emailGuardado,
          altaInvitado: !loggedIn,
        });
        clearCart();
        if (loggedIn) await refresh();
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'No se pudo confirmar el pago 3D Secure');
      })
      .finally(() => {
        if (!cancelled) setConfirmando3ds(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openpayReturnId, clearCart, loggedIn, refresh]);

  const handleModoChange = (next: ModoCompra) => {
    setModo(next);
    setCorreoExistente(false);
    setError(null);
    if (next === 'cliente' && cliente) {
      setForm(clienteToCheckoutForm(cliente));
    } else if (next === 'invitado') {
      setForm(initialForm);
    }
  };

  const verificarCorreoInvitado = async (email: string) => {
    if (modo !== 'invitado') {
      setCorreoExistente(false);
      return false;
    }
    const value = email.trim().toLowerCase();
    if (!value || !value.includes('@')) {
      setCorreoExistente(false);
      return false;
    }
    try {
      const res = await api.authVerificarCorreo(value);
      setCorreoExistente(!!res.existe);
      if (res.existe) {
        setError(res.mensaje ?? 'Este correo ya es cliente Alarcom.');
        return true;
      }
      setError((actual) =>
        actual && actual.includes('ya es cliente') ? null : actual,
      );
      return false;
    } catch {
      return false;
    }
  };

  const base = subtotal();
  const grandTotal = form.requiere_factura ? totalConIva(base) : base;

  const update = (field: keyof CheckoutForm, value: string | boolean) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const buildPayload = useMemo(
    () => ({
      modo_compra: modo,
      cliente: {
        nombre: form.nombre,
        email: form.email,
        telefono: form.telefono,
      },
      envio: {
        calle: form.calle,
        numero: form.numero,
        colonia: form.colonia,
        municipio: form.municipio,
        estado: form.estado,
        codigo_postal: form.codigo_postal,
      },
      requiere_factura: form.requiere_factura,
      facturacion: form.requiere_factura
        ? {
            rfc_fiscal: form.rfc_fiscal,
            razon_social_fiscal: form.razon_social_fiscal,
            uso_cfdi: form.uso_cfdi,
            regimen_fiscal: form.regimen_fiscal,
            codigo_postal_fiscal: form.codigo_postal_fiscal ?? form.codigo_postal,
          }
        : null,
      items: items.map((i) => ({
        tipo: i.tipo,
        refId: i.refId,
        cantidad: i.cantidad,
        precio: i.precio,
        origen: i.origen ?? 'alarcom',
        idExterno: i.idExterno ?? null,
        skuExterno: i.skuExterno ?? null,
      })),
    }),
    [form, items, modo],
  );

  const datosFiscalesFijos = modo === 'cliente';

  const formValid =
    !!form.nombre &&
    !!form.email &&
    !!form.telefono &&
    !!form.calle &&
    !!form.numero &&
    !!form.colonia &&
    !!form.municipio &&
    !!form.estado &&
    !!form.codigo_postal &&
    (!form.requiere_factura || (!!form.rfc_fiscal && !!form.razon_social_fiscal)) &&
    !(modo === 'invitado' && correoExistente) &&
    aceptaTerminos;

  const mostrarError = (message: string) => {
    setError(message);
    if (modo === 'invitado' && message.includes('ya es cliente')) {
      setCorreoExistente(true);
    }
  };

  const handlePedidoPendiente = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    try {
      if (await verificarCorreoInvitado(form.email)) {
        return;
      }
      const result = await api.crearPedido(buildPayload);
      setExito({
        folio: result.folio,
        idVenta: result.id_venta,
        email: form.email,
        altaInvitado: modo === 'invitado',
      });
      clearCart();
      if (loggedIn) await refresh();
    } catch (err) {
      mostrarError(err instanceof Error ? err.message : 'Error al registrar el pedido');
    } finally {
      setEnviando(false);
    }
  };

  const handleOpenpaySuccess = async (result: { folio: string; id_venta: number }) => {
    setExito({
      folio: result.folio,
      idVenta: result.id_venta,
      pagado: true,
      email: form.email,
      altaInvitado: modo === 'invitado',
    });
    clearCart();
    if (loggedIn) await refresh();
  };

  if (confirmando3ds) {
    return (
      <div className="container-app max-w-lg py-20 text-center">
        <h1 className="section-title">Confirmando pago</h1>
        <p className="mt-4 text-surface-800/60">Validando la autenticación 3D Secure con OpenPay...</p>
      </div>
    );
  }

  if (items.length === 0 && !exito) {
    return (
      <div className="container-app max-w-lg py-20 text-center">
        <h1 className="section-title">Checkout</h1>
        {error ? (
          <>
            <p className="mt-4 text-red-700">{error}</p>
            <Link to="/catalogo" className="btn-primary mt-8">
              Volver al catálogo
            </Link>
          </>
        ) : (
          <>
            <p className="mt-4 text-surface-800/60">Tu carrito está vacío</p>
            <Link to="/catalogo" className="btn-primary mt-8">
              Ir al catálogo
            </Link>
          </>
        )}
      </div>
    );
  }

  if (exito) {
    return (
      <div className="container-app max-w-lg py-20 text-center">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-10">
          <h1 className="font-display text-2xl font-bold text-emerald-800">
            ¡Pedido recibido!
          </h1>
          <p className="mt-2 text-emerald-700">
            Folio: <strong>{exito.folio}</strong>
            {exito.idVenta ? (
              <> · Venta sistema #{exito.idVenta}</>
            ) : null}
          </p>
          <p className="mt-4 text-sm text-emerald-600">
            {exito.pagado
              ? 'El pago con tarjeta fue confirmado. El pedido ya está registrado en Alarcom.'
              : 'El pedido ya está en Alarcom. Te contactaremos para confirmar el pago y el envío.'}
          </p>
          {exito.altaInvitado && exito.email ? (
            <p className="mt-4 text-sm text-emerald-800">
              Quedaste registrado como cliente con <strong>{exito.email}</strong>. Crea tu
              contraseña para ver tus pedidos la próxima vez.
            </p>
          ) : null}
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            {loggedIn && exito.folio && (
              <Link to={`/cuenta/pedidos/${encodeURIComponent(exito.folio)}`} className="btn-primary">
                Ver en mi cuenta
              </Link>
            )}
            {exito.altaInvitado && exito.email && (
              <Link
                to={`/recuperar?correo=${encodeURIComponent(exito.email)}`}
                className="btn-primary"
              >
                Crear mi contraseña
              </Link>
            )}
            <Link to="/" className="btn-secondary">
              Volver al inicio
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <CatalogBanner title="Checkout" subtitle="Datos de envío y pago" />
      <div className="page-section">
        <div className="container-app">
          {!loggedIn && (
            <CheckoutModeSelector modo={modo} onChange={handleModoChange} loggedIn={loggedIn} />
          )}
          {modo === 'cliente' && cliente && (
            <p className="mb-4 text-sm text-surface-600">
              Comprando como <strong>{cliente.nombre}</strong> (cuenta {cliente.cuenta})
            </p>
          )}
          <form onSubmit={handlePedidoPendiente} className="grid gap-8 lg:grid-cols-5">
            {error && (
              <div className="lg:col-span-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <p>{error}</p>
                {correoExistente && (
                  <p className="mt-2">
                    <Link
                      to={`/ingresar?redirect=/checkout&correo=${encodeURIComponent(form.email.trim())}`}
                      className="font-medium underline"
                    >
                      Iniciar sesión
                    </Link>
                    {' · '}
                    <Link
                      to={`/recuperar?correo=${encodeURIComponent(form.email.trim())}`}
                      className="font-medium underline"
                    >
                      Crear o recuperar contraseña
                    </Link>
                  </p>
                )}
              </div>
            )}
            <div className="space-y-5 lg:col-span-3">
              <Panel title="Datos de contacto" icon={<User className="h-4 w-4 text-brand-600" />}>
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  className="input-field sm:col-span-2"
                  placeholder="Nombre completo *"
                  required
                  value={form.nombre}
                  onChange={(e) => update('nombre', e.target.value)}
                />
                <input
                  type="email"
                  className="input-field"
                  placeholder="Correo electrónico *"
                  required
                  value={form.email}
                  onChange={(e) => {
                    update('email', e.target.value);
                    if (correoExistente) setCorreoExistente(false);
                  }}
                  onBlur={() => {
                    void verificarCorreoInvitado(form.email);
                  }}
                />
                <input
                  type="tel"
                  className="input-field"
                  placeholder="Teléfono *"
                  required
                  value={form.telefono}
                  onChange={(e) => update('telefono', e.target.value)}
                />
              </div>
              </Panel>

              <Panel title="Dirección de envío" icon={<MapPin className="h-4 w-4 text-brand-600" />}>
              <div className="grid gap-4 sm:grid-cols-2">
                <input className="input-field sm:col-span-2" placeholder="Calle *" required value={form.calle} onChange={(e) => update('calle', e.target.value)} />
                <input className="input-field" placeholder="Número *" required value={form.numero} onChange={(e) => update('numero', e.target.value)} />
                <input className="input-field" placeholder="Colonia *" required value={form.colonia} onChange={(e) => update('colonia', e.target.value)} />
                <input className="input-field" placeholder="Municipio *" required value={form.municipio} onChange={(e) => update('municipio', e.target.value)} />
                <input className="input-field" placeholder="Estado *" required value={form.estado} onChange={(e) => update('estado', e.target.value)} />
                <input className="input-field sm:col-span-2" placeholder="C.P. *" required maxLength={5} value={form.codigo_postal} onChange={(e) => update('codigo_postal', e.target.value)} />
              </div>
              </Panel>

              <Panel>
              <label className="flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  checked={form.requiere_factura}
                  onChange={(e) => update('requiere_factura', e.target.checked)}
                  className="h-4 w-4 rounded border-surface-300 text-brand-600"
                />
                <span className="flex items-center gap-2 font-semibold">
                  <FileText className="h-4 w-4 text-brand-600" />
                  Requiero factura (CFDI)
                </span>
              </label>
              {form.requiere_factura && (
                <div className="mt-4 grid gap-4 sm:grid-cols-2">
                  <input
                    className="input-field"
                    placeholder="RFC *"
                    required
                    readOnly={datosFiscalesFijos}
                    disabled={datosFiscalesFijos}
                    value={form.rfc_fiscal ?? ''}
                    onChange={(e) => update('rfc_fiscal', e.target.value)}
                  />
                  <input
                    className="input-field sm:col-span-2"
                    placeholder="Razón social *"
                    required
                    readOnly={datosFiscalesFijos}
                    disabled={datosFiscalesFijos}
                    value={form.razon_social_fiscal ?? ''}
                    onChange={(e) => update('razon_social_fiscal', e.target.value)}
                  />
                  <input
                    className="input-field"
                    placeholder="Uso CFDI (ej. G03)"
                    readOnly={datosFiscalesFijos}
                    disabled={datosFiscalesFijos}
                    value={form.uso_cfdi ?? ''}
                    onChange={(e) => update('uso_cfdi', e.target.value)}
                  />
                  <input
                    className="input-field"
                    placeholder="Régimen fiscal"
                    readOnly={datosFiscalesFijos}
                    disabled={datosFiscalesFijos}
                    value={form.regimen_fiscal ?? ''}
                    onChange={(e) => update('regimen_fiscal', e.target.value)}
                  />
                  {datosFiscalesFijos && (
                    <p className="sm:col-span-2 text-xs text-surface-500">
                      Los datos fiscales de su cuenta no se pueden editar aquí. Para corregir RFC,
                      razón social o régimen, solicítelo a Alarcom en{' '}
                      <a href="mailto:ventas@alarcom.mx" className="font-medium text-brand-700 underline">
                        ventas@alarcom.mx
                      </a>
                      .
                    </p>
                  )}
                </div>
              )}
              </Panel>
            </div>

            <aside className="lg:col-span-2">
              <Panel className="sticky top-20" title="Resumen">
              <ul className="space-y-3 border-b border-surface-200 pb-4">
                {items.map((i) => (
                  <li key={i.id} className="flex justify-between text-sm">
                    <span className="truncate pr-2">
                      {i.cantidad}× {i.nombre}
                    </span>
                    <span className="shrink-0 font-medium">
                      {formatMXN(i.precio * i.cantidad)}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal (sin IVA)</span>
                  <span>{formatMXN(base)}</span>
                </div>
                <div className="flex justify-between text-surface-600">
                  <span>IVA (16%)</span>
                  <span>{formatMXN(calcularIva(base))}</span>
                </div>
                <div className="flex justify-between border-t pt-2 text-lg font-bold">
                  <span>{form.requiere_factura ? 'Total c/IVA' : 'Total a pagar'}</span>
                  <span className="text-brand-700">{formatMXN(grandTotal)}</span>
                </div>
                {!form.requiere_factura && (
                  <p className="text-xs text-surface-500">
                    Sin factura el cobro es por el subtotal sin IVA.
                  </p>
                )}
              </div>
              <div className="mt-6 space-y-4 border-t border-surface-200 pt-6">
                <h3 className="text-sm font-semibold text-surface-800">Método de pago</h3>
                <label className="flex cursor-pointer items-start gap-3 text-sm text-surface-700">
                  <input
                    type="checkbox"
                    checked={aceptaTerminos}
                    onChange={(e) => setAceptaTerminos(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-surface-300 text-brand-600"
                  />
                  <span>
                    Acepto los{' '}
                    <Link to="/terminos" target="_blank" className="font-medium text-brand-700 underline">
                      términos y condiciones
                    </Link>{' '}
                    y el{' '}
                    <Link to="/aviso-privacidad" target="_blank" className="font-medium text-brand-700 underline">
                      aviso de privacidad
                    </Link>
                    . El pago con tarjeta lo procesa Openpay.
                  </span>
                </label>
                <OpenpayCheckoutSection
                  payload={buildPayload}
                  formValid={formValid}
                  loggedIn={loggedIn}
                  onSuccess={handleOpenpaySuccess}
                  onError={mostrarError}
                />
                <div className="relative py-2 text-center text-xs text-surface-800/40">
                  <span className="bg-white px-2">o</span>
                  <div className="absolute inset-x-0 top-1/2 -z-10 border-t border-surface-200" />
                </div>
                <button
                  type="submit"
                  disabled={enviando || !formValid}
                  className="btn-secondary w-full"
                >
                  <CreditCard className="h-4 w-4" />
                  {enviando ? 'Procesando...' : 'Confirmar pedido (pago pendiente)'}
                </button>
                <p className="text-center text-xs text-surface-800/50">
                  Transferencia o pago posterior · le contactaremos para confirmar
                </p>
              </div>
              </Panel>
            </aside>
          </form>
        </div>
      </div>
    </>
  );
}
